import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';
import { Grid } from '@material-ui/core';

import TextField from '../inputs/text_field.js';
import PasswordField from '../inputs/password_field.js';
import EnumField from '../inputs/enum_field.js';
import PhoneField from '../inputs/phone_field.js';
import DateTimeField from '../inputs/date_time_field.js';
import WYSIWYGField from '../inputs/wysiwyg_field.js';
import DecimalField from '../inputs/decimal_field.js';
import ImageField from '../inputs/image_field.js';
import ModelField from "../inputs/model_field.js";
import FileField from "../inputs/file_field.js";
import Checkbox from "../inputs/checkbox.js";

import NestedModelField from './fields/nested_model_field.js';
import NestedModelsArrayField from './fields/nested_models_array_field.js';

const styles = theme => ({
  root: {},
  component: {

  },
  formFieldContainer: {
    marginBottom: 20
  }
});

const maxUnits = 12;

/**
 * Form fields item change handler
 * @callback FileFields~onItemChange
 * @param {Object} newItem item after changing
 * @param {FormFieldDescription} field last changed field
 * @param {any} newValue new value in changed field
 */

/**
 * Component for render group of fields for html forms
 * @name FormFields
 * @param {object} props React component properties
 * @param {Array.<FormFieldDescription>} props.formFields Form fields for render
 * @param {object} props.item Item, displayed in form
 * @param {FileFields~onItemChange} props.onItemChange Change handler
 * @param {object} [props.errorData] Error messages after submit form to server
 * @param {boolean} [props.allowAutoSave = false] Enabling auto saving for form fields. If it equals true, 
 * all changes for {@link TextField|TextField's}, {@link PasswordField|PasswordField's} and {@link WYSIWYGField|WYSIWYGField's} will be fired after delay
 * @param {number} [props.autoSaveTimeout = 500] Delay for saving life edit fields. Useful only if props.allowAutoSave == true
 * @param {number} [props.defaultWeight = 12] Default weight of inputs inside form fields
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @example
 * const errorData = { fields: {lastName: "can't be blank"} }
 * const fields = this.fields;
 * return (
 *   <FormFields onChange = { (newItem, field, newValue) => {
 *                 // Some actions
 *               } } 
 *               item = { {id: 1, name: "Name"} }
 *               errorData = { errorData }
 *               formFields = { fields } />
 * )
*/
class FormFields extends React.Component {

  constructor(props) {
    super(props);

    this.item = {...props.item};

    this.delayedForAutoSaveTypes = ["text", "email", "number", "password", "WYSIWYG"];

    this.state = {

    }

    this.fieldRefs = {

    }

    this.onChangeFieldValue = this.onChangeFieldValue.bind(this);
    this.changeValue        = this.changeValue.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    return {
      formFields: [ ...props.formFields ],
      errorData: { ...props.errorData },
      readOnly: props.readOnly
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return JSON.stringify(this.state.formFields) !== JSON.stringify(nextState.formFields) ||
           JSON.stringify(this.state.errorData) !== JSON.stringify(nextState.errorData) ||
           this.state.readOnly !== nextState.readOnly;
  }

  componentDidUpdate(prevProps, prevState) {
    if(JSON.stringify(this.item) !== JSON.stringify(prevProps.item)) {
      this.item = { ...this.props.item };
      this.forceUpdate();
    }
  }

  onChangeFieldValue(field, newValue) {
    const { allowAutoSave } = this.props;
    if(!allowAutoSave) {
      this.changeValue(field, newValue);
      return;
    }

    if(this.delayedForAutoSaveTypes.includes(field.type)) {
      if(this.timeoutToken) {
        clearTimeout(this.timeoutToken);
      }
      this.timeoutToken = setTimeout(() => {
        clearTimeout(this.timeoutToken);
        this.timeoutToken = undefined;

        this.changeValue(field, newValue);
      }, this.props.autoSaveTimeout)
    }
    else {
      this.changeValue(field, newValue);
    }
  }

  changeValue(field, newValue) {
    const { onItemChange } = this.props;

    this.item[field.name] = newValue;
    if(field.foreignKey) {
      const primaryKey = field.primaryKey || "id";
      this.item[field.foreignKey] = newValue ? newValue[primaryKey] : null;
    }
    
    if(onItemChange) {
      let newItem = {...{}, ...this.item};
      onItemChange(newItem, field, newValue);
    }
  }

  formField(field) {
    const { onChange } = this.props;
    const { autoSaveTimeout, allowAutoSave, onValueTimeUp } = this.props;

    const { errorData, readOnly } = this.state;
    const { fields: fieldsErrorData = {} } = errorData;

    if(field.renderFunction) {
      return field.renderFunction(this.item, field, errorData, onChange);
    }

    let value = this.item[field.name];
    let { name, alternateName } = field;
    if(alternateName !== undefined && alternateName !== null) {
      name = alternateName;
    }
    
    if(field.type === "text" || 
       field.type === "number" || 
       field.type === "email") { 
      return <TextField value = { value }
                        onChange = { (value) => this.onChangeFieldValue(field, value) }
                        errors = { fieldsErrorData[field.name] }
                        { ...field }
                        readOnly = { readOnly || field.readOnly }
                        name = { name } />
    }
    if(field.type === "password") {
      return <PasswordField value = { value }
                            onChange = { (value) => this.onChangeFieldValue(field, value) }
                            errors = { fieldsErrorData[field.name] }
                            { ...field }
                            readOnly = { readOnly || field.readOnly }
                            name = { name  } />
    }
    if(field.type === "enum") {
      return <EnumField value = { value } 
                        onChange = { (value) => this.onChangeFieldValue(field, value) }
                        errors = { fieldsErrorData[field.name] }
                        { ...field }
                        readOnly = { readOnly || field.readOnly }
                        name = { name  }/>
    }
    if(field.type === "dateTime") {
      return <DateTimeField value = { value } 
                            onChange = { (value) => this.onChangeFieldValue(field, value) } 
                            errors = { fieldsErrorData[field.name] }
                            { ...field } 
                            readOnly = { readOnly || field.readOnly }
                            name = { name  }/>
    }
    if(field.type === "phone") {
      return <PhoneField value = { value } 
                         onChange = { (value) => this.onChangeFieldValue(field, value) } 
                         errors = { fieldsErrorData[field.name] }
                         { ...field }
                         readOnly = { readOnly || field.readOnly } 
                         name = { name  }/>
    }
    if(field.type === "nestedModel") {
      let formFields = [];
      if(field.fields) {
        formFields = field.fields(field, value);
      }
      else if(field.model) {
        formFields = field.model.nestedFormFields(field, {item: value});
      }
      return <NestedModelField value = { value } 
                               onChange = { (value) => this.onChangeFieldValue(field, value) } 
                               autoSaveTimeout = { autoSaveTimeout }
                               allowAutoSave = { allowAutoSave }
                               parentField = { field }
                               formFields = { formFields }
                               errorData = { errorData[field.name]  } 
                               defaultWeight = { this.props.defaultWeight }
                               { ...field }
                               readOnly = { readOnly || field.readOnly } 
                               name = { name  }/>
    }
    if(field.type === "nestedModelsArray") {
      let formFields = [];
      if(field.fields) {
        formFields = field.fields(field, value);
      }
      else if(field.model) {
        formFields = field.model.nestedFormFields(field, {item: value});
      }
      let objectErrors = errorData[field.name] || []
      return <NestedModelsArrayField values = { value } 
                                     onChange = { (value) => this.onChangeFieldValue(field, value) } 
                                     autoSaveTimeout = { autoSaveTimeout }
                                     allowAutoSave = { allowAutoSave }
                                     parentField = { field }
                                     formFields = { formFields }
                                     errors = { fieldsErrorData[field.name] }
                                     objectErrors = { objectErrors } 
                                     defaultWeight = { field.defaultWeight || this.props.defaultWeight }
                                     { ...field }
                                     readOnly = { readOnly || field.readOnly } 
                                     name = { name  }/>
    }
    if(field.type === "WYSIWYG") {
      return <WYSIWYGField value = { value }
                           onChange = { (value) => this.onChangeFieldValue(field, value) }
                           errors = { fieldsErrorData[field.name] }
                           { ...field }
                           readOnly = { readOnly || field.readOnly }
                           name = { name  } />
    }
    if(field.type === "decimal") {
      return <DecimalField value = { value }
                           onChange = { (value) => this.onChangeFieldValue(field, value) }
                           errors = { fieldsErrorData[field.name] }
                           { ...field }
                           readOnly = { readOnly || field.readOnly }
                           name = { name  } />
    }
    if(field.type === "image") {
      return <ImageField value = { value }
                         onChange = { (value) => this.onChangeFieldValue(field, value) }
                         errors = { fieldsErrorData[field.name] }
                         onAutoDelete = { () => onValueTimeUp(value) }
                         { ...field } 
                         readOnly = { readOnly || field.readOnly }
                         name = { name  }/>
    }
    if(field.type === "file") {
      return <FileField value = { value }
                        onChange = { (value) => this.onChangeFieldValue(field, value) }
                        errors = { fieldsErrorData[field.name] }
                        onAutoDelete = { () => onValueTimeUp(value) }
                        { ...field } 
                        readOnly = { readOnly || field.readOnly }
                        name = { name  }/>
    }
    if(field.type === "model") {
      return <ModelField value = { value }
                         valueId = { this.item[field.foreignKey] }
                         onChange = { (value) => this.onChangeFieldValue(field, value) }
                         errors = { fieldsErrorData[field.name] }
                         { ...field }
                         readOnly = { readOnly || field.readOnly } 
                         name = { name  }/>
    }
    if(field.type === "boolean") {
      return <Checkbox checked = { value } 
                       onChange = { (value) => this.onChangeFieldValue(field, value) }
                       { ...field } 
                       readOnly = { readOnly || field.readOnly } 
                       name = { name  }/>
    }
    return <React.Fragment />
  }

  render() {
    const { classes, className } = this.props;

    const { formFields } = this.state;

    if(formFields.length === 0) {
      return <React.Fragment />
    }

    return <div className = { clsx(classes.root, className) } >
      <Grid container >
        { this.constructFormFieldsGrid() }
      </Grid>
    </div>
  }

  constructFormFieldsGrid() {
    const { formFields } = this.state;

    const { defaultWeight, classes } = this.props;

    if(formFields.length === 0) {
      return <React.Fragment />
    }
    
    let gridWeb = [];
    let remainsUnits = maxUnits;
    formFields.forEach((field) => {
      const { weight = defaultWeight, forceNextLine } = field;
      let component = this.formField(field);

      if(remainsUnits === 0) {
        remainsUnits = maxUnits;
      }
      else if(remainsUnits < weight) {
        gridWeb.push(<Grid item md =  { remainsUnits } key = { `${field.name}_spacer`} />)
        remainsUnits = maxUnits;
      }

      component = <Grid key = { field.name }
                        item
                        md =  { weight }
                        ref = { (ref) => {
                          this.fieldRefs[field.name] = ref;
                        }} >
        <div className = { classes.component } >
          { component }
        </div>
      </Grid>
      gridWeb.push(component);
      remainsUnits = remainsUnits - weight;
      if(forceNextLine && remainsUnits > 0) {
        gridWeb.push(<Grid item md =  { remainsUnits } key = { `${field.name}_spacer`} />)
        remainsUnits = 0;
      }
    })
    return gridWeb;
  }

}

FormFields.propTypes = {
  formFields: PropTypes.array.isRequired,
  item: PropTypes.object.isRequired,
  errorData: PropTypes.object,
  onItemChange: PropTypes.func,
  autoSaveTimeout: PropTypes.number,
  allowAutoSave: PropTypes.bool,
  defaultWeight: PropTypes.number,
  readOnly: PropTypes.bool,
}

FormFields.defaultProps = {
  autoSaveTimeout: 500,
  allowAutoSave: false,
  defaultWeight: 12,
  readOnly: false
}

export default withStyles(
  (theme) => ({
    ...styles(theme),
  }),
  { withTheme: true },
)(FormFields);
